import moment from 'moment';
import Swal from 'sweetalert2';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';
import { printDispatchDocument } from './printDispatchDocuments';

const SOURCE_OPTIONS = [
    { value: 1, label: 'يدوي', icon: 'ti-edit' },
    { value: 2, label: 'من طلب معتمد', icon: 'ti-clipboard-check' },
    { value: 3, label: 'مباشر (طوارئ)', icon: 'ti-emergency-bed' },
];

const STATUS_OPTIONS = [
    { value: 1, label: 'مسودة', tone: 'secondary' },
    { value: 2, label: 'مطبوع — في انتظار الاستلام', tone: 'info' },
    { value: 3, label: 'مستلم — في انتظار سند الدخول', tone: 'warning' },
    { value: 4, label: 'مكتمل', tone: 'success' },
    { value: 9, label: 'ملغي', tone: 'danger' },
];

const STATUS_DRAFT = 1;
const STATUS_ISSUE_PRINTED = 2;
const STATUS_FINAL_PRINTED = 3;
const STATUS_COMPLETED = 4;
const STATUS_CANCELLED = 9;

const STEPS = [
    { key: 'draft', title: 'إنشاء عملية صرف', short: 'مسودة', icon: 'ti-file-plus' },
    { key: 'issue', title: 'طباعة سند الصرف', short: 'سند الصرف', icon: 'ti-printer' },
    { key: 'review', title: 'مراجعة المستلم وموافقته', short: 'موافقة المستلم', icon: 'ti-check' },
    { key: 'finalInvoice', title: 'طباعة الفاتورة النهائية', short: 'فاتورة نهائية', icon: 'ti-receipt' },
    { key: 'receipt', title: 'تأكيد الاستلام وسند الدخول', short: 'سند الدخول', icon: 'ti-package' },
];

export default {
    name: 'DispatchManagement',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية للوصول إلى الصرف');
            this.href('/Home');
            return;
        }
        await Promise.all([this.loadBranches(), this.loadStockings(), this.loadProducts()]);
        await this.GetStatistics();
        this.GetInfo(1);
    },

    computed: {
        canViewPage() {
            if (!this.loginDetails) return false;
            if (this.hasInventoryAndStockAccessUi()) return true;
            if (this.isMinistryPharmacyDepartmentRole()) return true;
            if (this.isFacilityCenterRole() && this.loginDetails.branchesId) return true;
            return false;
        },
        canManage() {
            return this.hasInventoryAndStockAccessUi();
        },
        canConfirmReceipt() {
            return this.isFacilityCenterRole() && !!this.loginDetails && !!this.loginDetails.branchesId;
        },
        statsCards() {
            const s = this.Statistics || {};
            return [
                this.statCard('عمليات صرف قيد التنفيذ', s.inProgress, 'warning', 'ti ti-clock'),
                this.statCard('عمليات صرف مكتملة', s.completed, 'success', 'ti ti-circle-check'),
                this.statCard('عمليات صرف ملغية', s.cancelled, 'danger', 'ti ti-circle-x'),
                this.statCard('إجمالي الكمية المصروفة (الشهر)', s.monthlyQuantity, 'info', 'ti ti-package'),
            ];
        },
        sourceOptions() { return SOURCE_OPTIONS; },
        statusOptions() { return STATUS_OPTIONS; },
        steps() { return STEPS; },
        ministryStockings() {
            return (this.stockingsList || []).filter(s => !s.branchesId && s.status === 1);
        },
        facilityBranches() {
            return (this.branchesList || []).filter(b => b.status !== 9);
        },
        wizardLabel() {
            const t = this.wizardSourceType;
            const opt = SOURCE_OPTIONS.find(x => x.value === t);
            return opt ? opt.label : '—';
        },
        modalLinesTotal() {
            return (this.modalLines || []).reduce((s, l) => s + (Number(l.Quantity) || 0), 0);
        },
        statusBadgeClass() {
            return (status) => {
                const opt = STATUS_OPTIONS.find(x => x.value === Number(status));
                return opt ? `badge bg-label-${opt.tone}` : 'badge bg-label-secondary';
            };
        },
        statusLabelOf() {
            return (status) => {
                const opt = STATUS_OPTIONS.find(x => x.value === Number(status));
                return opt ? opt.label : '—';
            };
        },
        sourceChipClass() {
            return (t) => {
                if (Number(t) === 2) return 'source-chip source-fromorder';
                if (Number(t) === 3) return 'source-chip source-direct';
                return 'source-chip';
            };
        },
        sourceLabelOf() {
            return (t) => {
                const opt = SOURCE_OPTIONS.find(x => x.value === Number(t));
                return opt ? opt.label : '—';
            };
        },
        canSaveDetailCommercial() {
            return false;
        },
        isOrderDetailView() {
            return this.currentDetail && Number(this.pickField(this.currentDetail, 'dispatchSourceType', 'DispatchSourceType')) === 2;
        },
        isDetailDraft() {
            return this.currentDetail && Number(this.pickField(this.currentDetail, 'status', 'Status')) === STATUS_DRAFT;
        },
        canAllocateBatches() {
            return this.canManage && this.isOrderDetailView && this.isDetailDraft;
        },
        selectedOrderItem() {
            const id = this.selectedOrderItemId;
            if (!id) return null;
            return (this.orderItemsSummary || []).find((it) => Number(it.nationalFormularyItemId) === Number(id)) || null;
        },
    },

    data() {
        return {
            ScreenTitle: 'الصرف (Dispatch)',
            pageNo: 1,
            pageSize: 10,
            pages: 0,
            showDetailView: false,

            FilterFacility: '',
            FilterStatus: '',
            FilterFrom: '',
            FilterTo: '',
            Search: '',

            Info: [],
            Statistics: {},

            branchesList: [],
            stockingsList: [],
            productsList: [],
            approvedOrders: [],
            approvedOrdersLoading: false,

            wizardSourceType: 1,

            modalSourceStockingId: '',
            modalFacilityBranchesId: '',
            modalNote: '',
            modalLines: [],
            modalAttachments: [],
            modalApprovedOrderId: '',
            submitting: false,

            currentDetail: null,
            detailLoading: false,
            detailLines: [],
            detailSaving: false,
            orderItemsSummary: [],
            selectedOrderItemId: null,
            selectedItemBatches: [],
            selectedBatchesLoading: false,
            addingBatch: false,
        };
    },

    filters: {
        moment(d) { return d ? moment(d).format('DD/MM/YYYY') : '—'; },
        momentTime(d) { return d ? moment(d).format('DD/MM/YYYY HH:mm') : '—'; },
    },

    methods: {
        tableRowNumber(i) {
            return ((Number(this.pageNo) - 1) * Number(this.pageSize)) + i + 1;
        },

        extractError(err) {
            if (err && err.response && err.response.data) {
                return typeof err.response.data === 'string' ? err.response.data : (err.response.data.message || 'حدث خطأ غير متوقع');
            }
            return (err && err.message) || 'حدث خطأ غير متوقع';
        },

        async loadBranches() {
            try {
                const res = await this.$http.GetAllBranches();
                this.branchesList = (res && res.data && res.data.info) || [];
            } catch { this.branchesList = []; }
        },

        async loadStockings() {
            try {
                const res = await this.$http.GetAllStocking();
                const arr = (res && res.data && res.data.info) || (res && res.data) || [];
                this.stockingsList = Array.isArray(arr) ? arr : [];
            } catch { this.stockingsList = []; }
        },

        async loadProducts() {
            try {
                const res = await this.$http.GetAllProducts();
                const arr = (res && res.data && res.data.info) || (res && res.data) || [];
                this.productsList = Array.isArray(arr) ? arr : [];
            } catch { this.productsList = []; }
        },

        async loadApprovedOrders() {
            this.approvedOrdersLoading = true;
            try {
                const res = await this.$http.GetDispatchApprovedOrders();
                this.approvedOrders = (res && res.data) || [];
            } catch { this.approvedOrders = []; } finally { this.approvedOrdersLoading = false; }
        },

        ApplyFilters() { this.GetInfo(1); },

        ResetFilters() {
            this.FilterFacility = '';
            this.FilterStatus = '';
            this.FilterFrom = '';
            this.FilterTo = '';
            this.Search = '';
            this.GetInfo(1);
        },

        GetStatistics() {
            return this.$http.GetDispatchStatistics()
                .then(res => { this.Statistics = (res && res.data) || {}; })
                .catch(() => { this.Statistics = {}; });
        },

        GetInfo(pageNo) {
            this.pageNo = pageNo || this.pageNo || 1;
            if (!this.Search) this.$blockUI.Start();
            this.$http.GetDispatch(
                this.pageNo,
                this.pageSize,
                this.Search,
                this.FilterFacility || null,
                this.FilterStatus === '' ? null : Number(this.FilterStatus),
                this.FilterFrom || null,
                this.FilterTo || null,
            ).then(res => {
                if (!this.Search) this.$blockUI.Stop();
                this.Info = (res.data && res.data.info) || [];
                this.pages = (res.data && res.data.count) || 0;
            }).catch(() => {
                if (!this.Search) this.$blockUI.Stop();
                this.Info = [];
                this.pages = 0;
            });
        },

        OpenWizard() {
            this.wizardSourceType = 1;
            this.modalSourceStockingId = '';
            this.modalFacilityBranchesId = '';
            this.modalNote = '';
            this.modalLines = [this.buildEmptyLine()];
            this.modalAttachments = [];
            this.modalApprovedOrderId = '';
            this.approvedOrders = [];
        },

        async OnWizardContinue() {
            if (!this.wizardSourceType) return;
            const elClose = document.getElementById('CloseDispatchWizardModal');
            if (this.wizardSourceType === 2) {
                await this.loadApprovedOrders();
            }
            if (elClose) elClose.click();
            this.$nextTick(() => {
                const formId = this.wizardSourceType === 2
                    ? 'DispatchFromOrderModal'
                    : (this.wizardSourceType === 3 ? 'DispatchDirectModal' : 'DispatchManualModal');
                const trigger = document.getElementById(formId + 'Btn');
                if (trigger) trigger.click();
            });
        },

        buildEmptyLine() {
            return {
                ProductsId: '',
                SerialNumber: '',
                ExpiryDate: '',
                Quantity: 0,
                UnitPrice: '',
            };
        },

        AddLine() {
            this.modalLines.push(this.buildEmptyLine());
        },

        RemoveLine(idx) {
            this.modalLines.splice(idx, 1);
            if (this.modalLines.length === 0) this.modalLines.push(this.buildEmptyLine());
        },

        OnAttachmentChange(event) {
            const files = (event && event.target && event.target.files) || [];
            const collected = [];
            const readers = [];
            for (let i = 0; i < files.length; i++) {
                const f = files[i];
                readers.push(new Promise((resolve) => {
                    const r = new FileReader();
                    r.onload = () => {
                        collected.push({ FileName: f.name, ContentType: f.type, Base64Content: r.result });
                        resolve();
                    };
                    r.onerror = resolve;
                    r.readAsDataURL(f);
                }));
            }
            Promise.all(readers).then(() => {
                this.modalAttachments = this.modalAttachments.concat(collected);
            });
        },

        RemoveAttachment(idx) {
            this.modalAttachments.splice(idx, 1);
        },

        validateModalLines() {
            if (!this.modalLines || this.modalLines.length === 0) return 'الرجاء إضافة صنف واحد على الأقل.';
            for (const ln of this.modalLines) {
                if (!ln.ProductsId) return 'الرجاء اختيار جميع الأصناف.';
                if (!ln.Quantity || Number(ln.Quantity) <= 0) return 'الرجاء إدخال كمية صحيحة لكل صنف.';
            }
            return null;
        },

        buildLinesPayload() {
            return this.modalLines.map(ln => ({
                ProductsId: Number(ln.ProductsId),
                Quantity: Number(ln.Quantity),
                UnitPrice: ln.UnitPrice === '' || ln.UnitPrice == null ? null : Number(ln.UnitPrice),
                SerialNumber: ln.SerialNumber || null,
                ExpiryDate: ln.ExpiryDate || null,
            }));
        },

        SubmitManual() {
            if (!this.modalSourceStockingId || !this.modalFacilityBranchesId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'الرجاء اختيار المخزن المصدر والجهة المستفيدة');
                return;
            }
            const err = this.validateModalLines();
            if (err) { this.$helper.ShowMessage('warning', 'تنبيه', err); return; }
            const payload = {
                SourceStockingId: Number(this.modalSourceStockingId),
                FacilityBranchesId: Number(this.modalFacilityBranchesId),
                Note: this.modalNote || '',
                Lines: this.buildLinesPayload(),
            };
            this.submitting = true;
            this.$blockUI.Start();
            this.$http.DispatchCreateManual(payload)
                .then(() => {
                    this.$blockUI.Stop(); this.submitting = false;
                    this.$helper.ShowMessage('success', 'نجاح', 'تم إنشاء الصرف اليدوي');
                    this.$helper.DestroyElemntById('CloseDispatchManualModal');
                    this.GetInfo(1); this.GetStatistics();
                })
                .catch(err => {
                    this.$blockUI.Stop(); this.submitting = false;
                    this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                });
        },

        SubmitDirect() {
            if (!this.modalSourceStockingId || !this.modalFacilityBranchesId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'الرجاء اختيار المخزن المصدر والجهة المستفيدة');
                return;
            }
            if (!this.modalAttachments.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'الصرف المباشر يتطلب رفع المستندات التعزيزية.');
                return;
            }
            const err = this.validateModalLines();
            if (err) { this.$helper.ShowMessage('warning', 'تنبيه', err); return; }
            const payload = {
                SourceStockingId: Number(this.modalSourceStockingId),
                FacilityBranchesId: Number(this.modalFacilityBranchesId),
                Note: this.modalNote || '',
                Lines: this.buildLinesPayload(),
                Attachments: this.modalAttachments,
            };
            this.submitting = true;
            this.$blockUI.Start();
            this.$http.DispatchCreateDirect(payload)
                .then(() => {
                    this.$blockUI.Stop(); this.submitting = false;
                    this.$helper.ShowMessage('success', 'نجاح', 'تم إنشاء الصرف المباشر');
                    this.$helper.DestroyElemntById('CloseDispatchDirectModal');
                    this.GetInfo(1); this.GetStatistics();
                })
                .catch(err => {
                    this.$blockUI.Stop(); this.submitting = false;
                    this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                });
        },

        SubmitFromOrder() {
            if (!this.modalApprovedOrderId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'الرجاء اختيار طلب معتمد');
                return;
            }
            this.submitting = true;
            this.$blockUI.Start();
            this.$http.DispatchCreateFromOrder(this.modalApprovedOrderId)
                .then(() => {
                    this.$blockUI.Stop(); this.submitting = false;
                    this.$helper.ShowMessage('success', 'نجاح', 'تم إنشاء الصرف — افتح التفاصيل لمراجعة الأصناف');
                    this.$helper.DestroyElemntById('CloseDispatchFromOrderModal');
                    this.GetInfo(1); this.GetStatistics();
                })
                .catch(err => {
                    this.$blockUI.Stop(); this.submitting = false;
                    this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                });
        },

        async loadBatchInfoForProduct(productsId, stockingId, existingSerial) {
            if (!productsId || !stockingId) return { batch: null, warehouseQty: 0 };
            try {
                const res = await this.$http.GetDispatchAvailableBatches(productsId, stockingId);
                const batches = (res && res.data) || [];
                const warehouseQty = batches.reduce((s, b) => s + (Number(this.pickField(b, 'availableQuantity', 'AvailableQuantity')) || 0), 0);
                let batch = null;
                if (existingSerial) {
                    batch = batches.find(b => (this.pickField(b, 'serialNumber', 'SerialNumber') || '') === existingSerial) || null;
                }
                if (!batch && batches.length) batch = batches[0];
                return { batch, warehouseQty };
            } catch {
                return { batch: null, warehouseQty: 0 };
            }
        },

        formatDetailDate(d) {
            if (!d) return '—';
            return moment(d).format('DD/MM/YYYY');
        },

        async enrichDetailLines() {
            const detail = this.currentDetail;
            const rawLines = (detail && (detail.lines || detail.Lines)) || [];
            if (!detail) {
                this.detailLines = [];
                this.orderItemsSummary = [];
                this.selectedOrderItemId = null;
                this.selectedItemBatches = [];
                return;
            }

            let orderRawLines = [];
            let orderItems = [];
            const orderId = this.pickField(detail, 'sourceMedicineOrderId', 'SourceMedicineOrderId');
            const sourceType = Number(this.pickField(detail, 'dispatchSourceType', 'DispatchSourceType'));
            const stockingId = this.pickField(detail, 'sourceStockingId', 'SourceStockingId');

            if (orderId && sourceType === 2) {
                try {
                    const res = await this.$http.GetDispatchOrderItems(orderId);
                    const data = (res && res.data) || {};
                    orderItems = data.items || data.Items || [];
                    orderRawLines = data.rawLines || data.RawLines || [];
                } catch { orderItems = []; orderRawLines = []; }
            }

            const byOrderLineId = {};
            orderRawLines.forEach((rl) => {
                const id = Number(this.pickField(rl, 'id', 'Id'));
                if (id) byOrderLineId[id] = rl;
            });
            orderItems.forEach((it) => {
                const primaryId = this.pickField(it, 'primaryOrderLineId', 'PrimaryOrderLineId');
                const lineIds = this.pickField(it, 'orderLineIds', 'OrderLineIds') || [];
                if (primaryId != null && !byOrderLineId[Number(primaryId)]) byOrderLineId[Number(primaryId)] = it;
                lineIds.forEach((lid) => {
                    if (!byOrderLineId[Number(lid)]) byOrderLineId[Number(lid)] = it;
                });
            });

            if (!rawLines.length) {
                this.detailLines = [];
                this.recomputeOrderItemsSummary(orderItems);
                return;
            }

            this.detailLines = await Promise.all(rawLines.map(async (ln) => {
                const productsId = this.pickField(ln, 'productsId', 'ProductsId');
                const lineOrderId = Number(this.pickField(ln, 'sourceMedicineOrderLineId', 'SourceMedicineOrderLineId'));
                const orderLine = lineOrderId ? byOrderLineId[lineOrderId] : null;

                let nationalFormularyItemId = this.pickField(ln, 'nationalFormularyItemId', 'NationalFormularyItemId')
                    || (orderLine ? this.pickField(orderLine, 'nationalFormularyItemId', 'NationalFormularyItemId') : null);
                let nationalCode = this.pickField(ln, 'nationalCode', 'NationalCode')
                    || (orderLine ? this.pickField(orderLine, 'nationalCode', 'NationalCode') : null);

                const productInfo = await this.resolveNationalFromProduct(productsId);
                if (productInfo) {
                    if (!nationalFormularyItemId) nationalFormularyItemId = productInfo.nationalFormularyItemId;
                    if (!nationalCode) nationalCode = productInfo.nationalCode;
                }

                const tradeName = (orderLine && this.pickField(orderLine, 'tradeName', 'TradeName'))
                    || this.pickField(ln, 'tradeName', 'TradeName', 'productName', 'ProductName')
                    || (productInfo && productInfo.tradeName)
                    || '—';

                const quantityRequired = orderLine
                    ? Number(this.pickField(orderLine, 'quantityApproved', 'QuantityApproved') || 0)
                    : Number(ln.quantity || 0);
                const dispenseQty = Number(ln.quantity || 0);

                const existingSerial = this.pickField(ln, 'serialNumber', 'SerialNumber');
                const { batch, warehouseQty } = await this.loadBatchInfoForProduct(productsId, stockingId, existingSerial);

                let serialNumber = existingSerial || this.pickField(batch, 'serialNumber', 'SerialNumber') || '';
                let expiryDate = this.pickField(ln, 'expiryDate', 'ExpiryDate')
                    || this.pickField(batch, 'expiryDate', 'ExpiryDate')
                    || '';
                let unitPrice = this.pickField(ln, 'unitPrice', 'UnitPrice');
                const batchPrice = this.pickField(batch, 'price', 'Price');
                if ((unitPrice === null || unitPrice === undefined || unitPrice === '') && batchPrice != null) {
                    unitPrice = batchPrice;
                }
                const lineTotal = unitPrice != null && unitPrice !== ''
                    ? Number(unitPrice) * dispenseQty
                    : (ln.lineTotal != null ? ln.lineTotal : null);

                return {
                    ...ln,
                    productsId,
                    nationalFormularyItemId,
                    nationalCode: nationalCode || '—',
                    tradeName,
                    quantityRequired,
                    warehouseQty,
                    dispenseQty,
                    serialNumber: serialNumber || '—',
                    expiryDate,
                    unitPrice,
                    lineTotal,
                };
            }));

            this.recomputeOrderItemsSummary(orderItems);
        },

        recomputeOrderItemsSummary(orderItems) {
            const dispensedByItem = {};
            (this.detailLines || []).forEach((ln) => {
                const nid = ln.nationalFormularyItemId;
                if (!nid) return;
                const k = Number(nid);
                dispensedByItem[k] = (dispensedByItem[k] || 0) + Number(ln.quantity || 0);
            });

            const list = (orderItems || []).map((it) => {
                const nid = Number(this.pickField(it, 'nationalFormularyItemId', 'NationalFormularyItemId'));
                const approved = Number(this.pickField(it, 'quantityApproved', 'QuantityApproved') || 0);
                const dispensed = dispensedByItem[nid] || 0;
                return {
                    nationalFormularyItemId: nid,
                    nationalCode: this.pickField(it, 'nationalCode', 'NationalCode') || '—',
                    scientificName: this.pickField(it, 'scientificName', 'ScientificName') || '—',
                    approved,
                    dispensed,
                    remaining: Math.max(0, approved - dispensed),
                };
            });

            this.orderItemsSummary = list;

            if (this.selectedOrderItemId
                && !list.some((it) => Number(it.nationalFormularyItemId) === Number(this.selectedOrderItemId))) {
                this.selectedOrderItemId = null;
                this.selectedItemBatches = [];
            }
        },

        async selectOrderItemForAllocation(item) {
            if (!item || !item.nationalFormularyItemId) return;
            this.selectedOrderItemId = Number(item.nationalFormularyItemId);
            await this.loadBatchesForSelectedItem();
        },

        async onOrderItemChanged() {
            if (!this.selectedOrderItemId) {
                this.clearSelectedOrderItem();
                return;
            }
            this.selectedOrderItemId = Number(this.selectedOrderItemId);
            await this.loadBatchesForSelectedItem();
        },

        clearSelectedOrderItem() {
            this.selectedOrderItemId = null;
            this.selectedItemBatches = [];
            this.selectedBatchesLoading = false;
        },

        async loadBatchesForSelectedItem() {
            const item = this.selectedOrderItem;
            const detail = this.currentDetail;
            if (!item || !detail) {
                this.selectedItemBatches = [];
                return;
            }
            const stockingId = this.pickField(detail, 'sourceStockingId', 'SourceStockingId');
            if (!stockingId) {
                this.selectedItemBatches = [];
                return;
            }
            this.selectedBatchesLoading = true;
            try {
                const res = await this.$http.GetDispatchProductBatchesByNationalItem(item.nationalFormularyItemId, stockingId);
                const batches = (res && res.data) || [];
                this.selectedItemBatches = batches.map((b) => ({
                    productsCountId: Number(this.pickField(b, 'productsCountId', 'ProductsCountId') || 0),
                    productsId: Number(this.pickField(b, 'productsId', 'ProductsId') || 0),
                    tradeName: this.pickField(b, 'tradeName', 'TradeName') || '—',
                    manufacturerName: this.pickField(b, 'manufacturerName', 'ManufacturerName') || '—',
                    packagingLabel: this.pickField(b, 'packagingLabel', 'PackagingLabel') || '—',
                    serialNumber: this.pickField(b, 'serialNumber', 'SerialNumber') || '',
                    batchNumber: this.pickField(b, 'batchNumber', 'BatchNumber') || this.pickField(b, 'serialNumber', 'SerialNumber') || '',
                    expiryDate: this.pickField(b, 'expiryDate', 'ExpiryDate') || '',
                    availableQuantity: Number(this.pickField(b, 'availableQuantity', 'AvailableQuantity') || 0),
                    unitPrice: this.pickField(b, 'unitPrice', 'UnitPrice', 'price', 'Price'),
                    qtyToAdd: 0,
                }));
            } catch {
                this.selectedItemBatches = [];
            } finally {
                this.selectedBatchesLoading = false;
            }
        },

        maxQtyForBatch(batch) {
            const item = this.selectedOrderItem;
            if (!item || !batch) return 0;
            return Math.min(Number(batch.availableQuantity || 0), Number(item.remaining || 0));
        },

        async addBatchAllocation(batch) {
            if (this.addingBatch) return;
            const item = this.selectedOrderItem;
            const detail = this.currentDetail;
            if (!item || !detail) return;
            const qty = Number(batch.qtyToAdd || 0);
            const max = this.maxQtyForBatch(batch);
            if (qty <= 0) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أدخل كمية صحيحة قبل الإضافة.');
                return;
            }
            if (qty > max) {
                this.$helper.ShowMessage('warning', 'تنبيه', `الكمية تتجاوز المتاح/المتبقي (${max}).`);
                return;
            }
            const orderId = this.pickField(detail, 'sourceMedicineOrderId', 'SourceMedicineOrderId');
            const sourceLineId = (() => {
                const ln = (this.detailLines || []).find(
                    (x) => Number(x.nationalFormularyItemId) === Number(item.nationalFormularyItemId),
                );
                return ln ? Number(this.pickField(ln, 'sourceMedicineOrderLineId', 'SourceMedicineOrderLineId')) : null;
            })();

            this.addingBatch = true;
            this.$blockUI.Start();
            try {
                await this.$http.DispatchAddBatchAllocation({
                    OrderId: Number(orderId),
                    NationalFormularyItemId: Number(item.nationalFormularyItemId),
                    ProductsCountId: Number(batch.productsCountId),
                    Quantity: qty,
                    DispatchId: Number(detail.id),
                    SourceMedicineOrderLineId: sourceLineId && sourceLineId > 0 ? sourceLineId : null,
                });
                this.$helper.ShowMessage('success', 'نجاح', 'تمت إضافة التشغيلة إلى عملية الصرف.');
                await this.reloadDetailAfterMutation();
            } catch (err) {
                this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
            } finally {
                this.$blockUI.Stop();
                this.addingBatch = false;
            }
        },

        async removeDetailLine(line) {
            if (!this.canAllocateBatches || !line || !line.id) return;
            const result = await Swal.fire({
                title: 'حذف السطر',
                text: 'هل تريد إزالة هذه التشغيلة من عملية الصرف؟',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'حذف',
                cancelButtonText: 'تراجع',
                customClass: { confirmButton: 'btn btn-danger me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
            });
            if (!result.isConfirmed) return;
            this.$blockUI.Start();
            try {
                await this.$http.DispatchRemoveLine({
                    DispatchId: Number(this.currentDetail.id),
                    LineId: Number(line.id),
                });
                this.$helper.ShowMessage('success', 'نجاح', 'تم حذف السطر.');
                await this.reloadDetailAfterMutation();
            } catch (err) {
                this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
            } finally {
                this.$blockUI.Stop();
            }
        },

        async reloadDetailAfterMutation() {
            if (!this.currentDetail || !this.currentDetail.id) return;
            const prevSelected = this.selectedOrderItemId;
            try {
                const res = await this.$http.GetDispatchOne(this.currentDetail.id);
                this.currentDetail = (res && res.data) || this.currentDetail;
                await this.enrichDetailLines();
                if (prevSelected) {
                    this.selectedOrderItemId = prevSelected;
                    await this.loadBatchesForSelectedItem();
                }
                this.GetInfo(this.pageNo);
            } catch (err) {
                this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
            }
        },

        async cleanupLegacyOrderSeedLinesIfNeeded() {
            const detail = this.currentDetail;
            if (!detail || !this.canManage) return false;
            const sourceType = Number(this.pickField(detail, 'dispatchSourceType', 'DispatchSourceType'));
            const status = Number(this.pickField(detail, 'status', 'Status'));
            if (sourceType !== 2 || status !== STATUS_DRAFT) return false;

            const lines = (detail.lines || detail.Lines || []);
            if (!lines.length) return false;

            const legacyLines = lines.filter((ln) => {
                const srcLineId = this.pickField(ln, 'sourceMedicineOrderLineId', 'SourceMedicineOrderLineId');
                const serial = this.pickField(ln, 'serialNumber', 'SerialNumber');
                const expiry = this.pickField(ln, 'expiryDate', 'ExpiryDate');
                const unitPrice = this.pickField(ln, 'unitPrice', 'UnitPrice');
                const hasNoBatchInfo = !serial && !expiry && (unitPrice === null || unitPrice === undefined || unitPrice === '');
                return srcLineId && hasNoBatchInfo;
            });

            if (!legacyLines.length) return false;

            for (const ln of legacyLines) {
                const lineId = this.pickField(ln, 'id', 'Id');
                if (!lineId) continue;
                try {
                    await this.$http.DispatchRemoveLine({
                        DispatchId: Number(this.pickField(detail, 'id', 'Id')),
                        LineId: Number(lineId),
                    });
                } catch {
                    // Skip failed line cleanup; detail load continues.
                }
            }
            return true;
        },

        pickField(obj, ...keys) {
            if (!obj) return undefined;
            for (const k of keys) {
                if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
            }
            return undefined;
        },

        async resolveNationalFromProduct(productsId) {
            if (!productsId) return null;
            try {
                const res = await this.$http.GetProductDetails(productsId);
                const p = (res && res.data && (res.data.info || res.data)) || {};
                return {
                    nationalFormularyItemId: this.pickField(p, 'nationalFormularyItemId', 'NationalFormularyItemId'),
                    nationalCode: this.pickField(p, 'nationalCode', 'NationalCode'),
                    scientificName: this.pickField(p, 'nationalFormularyName', 'NationalFormularyName', 'scientificName', 'ScientificName'),
                    tradeName: this.pickField(p, 'name', 'Name', 'productName', 'ProductName', 'tradeName', 'TradeName'),
                };
            } catch {
                return null;
            }
        },

        async OpenDetail(item) {
            this.showDetailView = true;
            this.currentDetail = null;
            this.detailLines = [];
            this.orderItemsSummary = [];
            this.selectedOrderItemId = null;
            this.selectedItemBatches = [];
            this.detailLoading = true;
            try {
                const res = await this.$http.GetDispatchOne(item.id);
                this.currentDetail = (res && res.data) || null;
                if (this.currentDetail) {
                    const cleaned = await this.cleanupLegacyOrderSeedLinesIfNeeded();
                    if (cleaned) {
                        const fresh = await this.$http.GetDispatchOne(item.id);
                        this.currentDetail = (fresh && fresh.data) || this.currentDetail;
                    }
                    await this.enrichDetailLines();
                }
            } catch (err) {
                this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                this.showDetailView = false;
            } finally {
                this.detailLoading = false;
            }
        },

        BackToList() {
            this.showDetailView = false;
            this.currentDetail = null;
            this.detailLines = [];
            this.orderItemsSummary = [];
            this.selectedOrderItemId = null;
            this.selectedItemBatches = [];
            this.detailLoading = false;
        },

        stepStateClass(stepKey) {
            const detail = this.currentDetail;
            if (!detail) return '';
            const status = Number(detail.status || 0);
            if (status === STATUS_CANCELLED) return '';
            const stepRank = STEPS.findIndex(s => s.key === stepKey);
            const statusRank = (() => {
                if (status === STATUS_DRAFT) return 0;
                if (status === STATUS_ISSUE_PRINTED) return 2;
                if (status === STATUS_FINAL_PRINTED) return 3;
                if (status === STATUS_COMPLETED) return 4;
                return 0;
            })();
            if (stepRank < statusRank) return 'is-done';
            if (stepRank === statusRank) return 'is-active';
            return '';
        },

        stepDate(stepKey) {
            const d = this.currentDetail;
            if (!d) return '';
            switch (stepKey) {
                case 'draft': return d.createdOn ? moment(d.createdOn).format('DD/MM/YYYY HH:mm') : '';
                case 'issue': return d.issueVoucherPrintedOn ? moment(d.issueVoucherPrintedOn).format('DD/MM/YYYY HH:mm') : '';
                case 'finalInvoice': return d.finalInvoicePrintedOn ? moment(d.finalInvoicePrintedOn).format('DD/MM/YYYY HH:mm') : '';
                case 'receipt': return d.receiptVoucherPrintedOn ? moment(d.receiptVoucherPrintedOn).format('DD/MM/YYYY HH:mm') : '';
                default: return '';
            }
        },

        PrintIssueVoucher(item) {
            Swal.fire({
                title: 'تأكيد طباعة سند الصرف',
                text: 'سيتم خصم الكميات من المخزن المصدر. هل تريد المتابعة؟',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد وطباعة',
                cancelButtonText: 'إلغاء',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchPrintIssueVoucher(item.id)
                    .then(async () => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم خصم الكميات. سيتم فتح نافذة الطباعة.');
                        this.GetInfo(this.pageNo); this.GetStatistics();
                        const res = await this.$http.GetDispatchOne(item.id);
                        if (res && res.data) printDispatchDocument(res.data, 'issue', { logoUrl: this.logoPath });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },

        PrintFinalInvoice(item) {
            Swal.fire({
                title: 'تأكيد طباعة الفاتورة النهائية',
                text: 'سيتم تحديث الحالة إلى "في انتظار سند الدخول".',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد وطباعة',
                cancelButtonText: 'إلغاء',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchPrintFinalInvoice(item.id)
                    .then(async () => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم تحديث الحالة. سيتم فتح نافذة الطباعة.');
                        this.GetInfo(this.pageNo); this.GetStatistics();
                        const res = await this.$http.GetDispatchOne(item.id);
                        if (res && res.data) printDispatchDocument(res.data, 'finalInvoice', { logoUrl: this.logoPath });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },

        ConfirmReceipt(item) {
            Swal.fire({
                title: 'تأكيد الاستلام وسند الدخول',
                text: 'سيتم إيداع الكميات في مخزن المرفق وإكمال العملية.',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-success me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد الاستلام',
                cancelButtonText: 'إلغاء',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchConfirmReceipt(item.id)
                    .then(async () => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم تأكيد الاستلام وإكمال العملية.');
                        this.GetInfo(this.pageNo); this.GetStatistics();
                        const res = await this.$http.GetDispatchOne(item.id);
                        if (res && res.data) printDispatchDocument(res.data, 'receipt', { logoUrl: this.logoPath });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },

        CancelDispatch(item) {
            Swal.fire({
                title: 'إلغاء عملية الصرف',
                text: 'الرجاء إدخال سبب الإلغاء',
                input: 'textarea',
                inputAttributes: { 'aria-label': 'سبب الإلغاء' },
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-danger me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'إلغاء العملية',
                cancelButtonText: 'تراجع',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchCancel(item.id, result.value || '')
                    .then(() => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم إلغاء العملية');
                        this.GetInfo(this.pageNo); this.GetStatistics();
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },

        ReprintDocument(detail, docKey) {
            if (!detail) return;
            printDispatchDocument(detail, docKey, { logoUrl: this.logoPath });
        },
    },
};
